import React from "react";
import { Tooltip } from "antd";

import { CATEGORIES_AFFICHAGE, COULEURS_CATEGORIES } from "./categoriesFlux";
import { COULEUR_EN_COURS, STATUTS_META } from "./filtresMap";

export const VUES = [
  { value: "treemap", label: "Treemap" },
  { value: "hex", label: "Carte hexagonale" },
  { value: "matrice", label: "Matrice des flux" },
  { value: "tableau", label: "Tableau" },
  { value: "3d", label: "Vue 3D" },
];

// Bandeau de page : titre + selecteur de vue.
export function MapEntete({ vue, onVue }) {
  return (
    <div className="map-entete">
      <h1>Map des traitements</h1>
      <div className="map-tabs" role="tablist" aria-label="Vue">
        {VUES.map((v) => (
          <button
            key={v.value}
            type="button"
            role="tab"
            aria-selected={vue === v.value}
            className={`map-tab${vue === v.value ? " on" : ""}`}
            onClick={() => onVue(v.value)}
          >
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Definitions des statuts : `court` sous le chiffre des cartes a confondre, `long` en infobulle
// et dans l'aide "?" de la barre de filtres (BI-682).
export const DEFINITIONS_STATUTS = {
  tous: {
    long: "Tous les traitements suivis : dernier build de chaque traitement des environnements actifs.",
  },
  erreur: { long: "Traitements dont le dernier build est en erreur." },
  warning: { long: "Traitements dont le dernier build est en warning." },
  encours: { long: "Traitements dont un build est en cours d'exécution." },
  grise: {
    court: "Traitement en retard",
    long: "Le serveur remonte, mais ce traitement n'a pas tourné dans la fréquence attendue par sa stratégie (expiré ou statut inconnu).",
  },
  serveurs: {
    court: "Serveur muet depuis 24 h",
    long: "Serveur déclaré en base dont aucun environnement actif (non annulé) n'a envoyé de build depuis 24 h : agent Elastic arrêté ou mal installé. Absent de la map, affiché en cliquant sur la carte.",
  },
  sans: {
    court: "Env. sans traitement rattaché",
    long: "Environnement présent sur la map (son serveur remonte) mais auquel aucun traitement n'est rattaché.",
  },
};

// Cartes de la page : [cle, libelle, champ, couleur]. "tous" efface le filtre de statut.
const CARTES = [
  ["tous", "Traitements", "total", "#195c84"],
  ["erreur", "En erreur", "erreur", STATUTS_META.erreur.couleur],
  ["warning", "Warnings", "warning", STATUTS_META.warning.couleur],
  ["encours", "En cours", "encours", COULEUR_EN_COURS],
  ["grise", "Grisés", "grise", STATUTS_META.grise.couleur],
  ["serveurs", "Serveurs non remontés", "serveurs", STATUTS_META.non_remonte.couleur],
  ["sans", "Env. sans traitement", "sans", STATUTS_META.sans.couleur],
];

// Cartes de chiffres cliquables = filtres de statut. kpi : sortie de calculerKpi ;
// statuts : filtres.statuts ; onChange({ statuts }).
export function CartesChiffres({ kpi, statuts, onChange }) {
  const actifs = Array.isArray(statuts) ? statuts : [];
  const clic = (cle) => {
    if (cle === "tous") onChange({ statuts: [] });
    else
      onChange({
        statuts: actifs.includes(cle) ? actifs.filter((s) => s !== cle) : [...actifs, cle],
      });
  };
  return (
    <div className="map-kpis" role="group" aria-label="Statuts">
      {CARTES.map(([cle, libelle, champ, couleur]) => {
        const on = cle === "tous" ? actifs.length === 0 : actifs.includes(cle);
        const def = DEFINITIONS_STATUTS[cle];
        return (
          <Tooltip key={cle} title={def.long} mouseEnterDelay={0.4}>
            <button
              type="button"
              className={`map-kpi${on && cle !== "tous" ? " on" : ""}${cle === "serveurs" ? " map-kpi-nr" : ""}`}
              aria-pressed={on}
              data-testid={`kpi-${cle}`}
              onClick={() => clic(cle)}
            >
              <div className="map-kpi-l">
                <span
                  className={`map-dot${cle === "serveurs" ? " map-dot-nr" : ""}`}
                  style={{ background: couleur }}
                />
                {libelle}
              </div>
              <div className="map-kpi-v">
                {kpi[champ] === null || kpi[champ] === undefined
                  ? cle === "serveurs"
                    ? "—"
                    : "0"
                  : Number(kpi[champ]).toLocaleString("fr-FR")}
              </div>
              {def.court && <div className="map-kpi-d">{def.court}</div>}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}

// Legende sous la vue : statuts, ou categories (etages) pour la 3D.
export function LegendeMap({ vue, nonRemonteActif = false }) {
  if (vue === "3d") {
    return (
      <div className="map-legende">
        Étages :
        {CATEGORIES_AFFICHAGE.map((c) => (
          <span key={c}>
            <span className="map-dot" style={{ background: COULEURS_CATEGORIES[c] }} />
            {c}
          </span>
        ))}
        <span>· anneau rouge = au moins une erreur</span>
        {nonRemonteActif && (
          <span>
            <span
              className="map-dot map-dot-nr"
              style={{ background: STATUTS_META.non_remonte.couleur }}
            />
            Non remonté (tour basse)
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="map-legende">
      {Object.entries(STATUTS_META)
        .filter(([k]) => k !== "non_remonte" || nonRemonteActif)
        .map(([k, m]) => (
          <span key={k}>
            <span
              className={`map-dot${k === "non_remonte" ? " map-dot-nr" : ""}`}
              style={{ background: m.couleur }}
            />
            {m.libelle}
          </span>
        ))}
      <span>
        <span className="map-dot running" style={{ background: COULEUR_EN_COURS }} />
        En cours (pulsation)
      </span>
    </div>
  );
}
